(() => {
if (window.__bundleBuilderLoaded) return;
window.__bundleBuilderLoaded = true;

const BundleDebug = {
  enabled() {
    return window.location.search.includes('bundle_debug=1') || window.localStorage?.getItem('bundle_debug') === '1';
  },

  log(...args) {
    if (!this.enabled()) return;
    console.log('[Bundle]', ...args);
  },

  warn(...args) {
    if (!this.enabled()) return;
    console.warn('[Bundle]', ...args);
  },

  table(data) {
    if (!this.enabled()) return;
    console.table(data);
  }
};

class BundleForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form[action*="/cart/add"]');
    this.submitButton = this.querySelector('[data-bundle-submit]');
    this.errorWrapper = this.querySelector('[data-bundle-error]');
    this.errorText = this.querySelector('[data-bundle-error-text]');
    this.quantityInput = this.querySelector('[data-bundle-quantity]');
    this.bundleIdInput = this.querySelector('[data-bundle-id]');
    this.bundleChildrenInput = this.querySelector('[data-bundle-children]');
    this.variantInput = this.form?.querySelector('input[name="id"]');
    this.variantSelects = this.querySelector('variant-selects');
    this.cartType = this.dataset.cartType;
    this.labels = {
      add: this.dataset.addLabel || 'Add to cart',
      soldOut: this.dataset.soldOutLabel || 'Sold out',
      sale: this.dataset.saleLabel || 'Sale',
      selectErrorTemplate: this.dataset.selectErrorTemplate || 'Please select one product from {group}.'
    };

    BundleDebug.log('BundleForm initialized', {
      sectionId: this.dataset.sectionId,
      productId: this.dataset.productId,
      hasForm: Boolean(this.form),
      groups: this.groups.length
    });

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    this.restoreSelections();
    this.addEventListener('change', this.onSelectionChange.bind(this));
    this.initVariantSelection();
  }

  get quantity() {
    const value = parseInt(this.quantityInput?.value || '1', 10);
    return Number.isNaN(value) || value < 1 ? 1 : value;
  }

  get groups() {
    return Array.from(this.querySelectorAll('[data-bundle-group]'));
  }

  selectedOptions() {
    return this.groups
      .map((group) => {
        const selected = group.querySelector('[data-bundle-option]:checked');
        if (!selected) return null;

        return {
          groupKey: selected.dataset.groupKey,
          groupTitle: selected.dataset.groupTitle,
          productId: selected.dataset.productId,
          productTitle: selected.dataset.productTitle,
          variantId: Number(selected.dataset.variantId),
          variantTitle: selected.dataset.variantTitle
        };
      })
      .filter(Boolean);
  }

  onSelectionChange(event) {
    if (!event.target.matches('[data-bundle-option]')) return;
    this.saveSelections();
  }

  initVariantSelection() {
    if (!this.variantSelects || !this.variantInput) return;

    const variantScript = this.variantSelects.querySelector('script[type="application/json"]');
    if (!variantScript) return;

    try {
      this.variants = JSON.parse(variantScript.textContent);
    } catch (error) {
      BundleDebug.warn('Unable to parse bundle parent variants', error);
      this.variants = [];
    }

    this.variantSelects.addEventListener('change', this.onVariantChange.bind(this));
    this.onVariantChange();
  }

  selectedVariantOptions() {
    if (!this.variantSelects) return [];

    return Array.from(this.variantSelects.querySelectorAll('select')).map((select) => select.value);
  }

  selectedVariant() {
    const selectedOptions = this.selectedVariantOptions();
    if (!selectedOptions.length || !this.variants?.length) return null;

    return this.variants.find((variant) => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
  }

  onVariantChange() {
    const variant = this.selectedVariant();
    if (!variant || !this.variantInput) return;

    this.variantInput.value = variant.id;
    this.updateParentPrice(variant);
    this.updateSubmitAvailability(variant);

    BundleDebug.log('Updated parent bundle variant', {
      id: variant.id,
      title: variant.title,
      available: variant.available,
      options: variant.options
    });
  }

  updateParentPrice(variant) {
    const priceContainer = this.querySelector('.bundle-product__price');
    if (!priceContainer) return;

    const price = formatBundleMoney(variant.price);
    const compareAtPrice = variant.compare_at_price ? formatBundleMoney(variant.compare_at_price) : '';

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceContainer.innerHTML = `
        <span class="bundle-product__price-current">${price}</span>
        <s class="bundle-product__price-compare">${compareAtPrice}</s>
        <span class="bundle-product__sale-badge">${this.escapeHtml(this.labels.sale)}</span>
      `;
      return;
    }

    priceContainer.innerHTML = `<span class="bundle-product__price-current">${price}</span>`;
  }

  updateSubmitAvailability(variant) {
    if (!this.submitButton) return;

    const label = this.submitButton.querySelector('span');
    this.submitButton.disabled = !variant.available;

    if (label) {
      label.textContent = variant.available ? this.labels.add : this.labels.soldOut;
    }
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  storageKey() {
    return `bundleSelections:${this.dataset.productId || this.dataset.sectionId}`;
  }

  saveSelections() {
    const selections = {};
    this.selectedOptions().forEach((option) => {
      selections[option.groupKey] = String(option.variantId);
    });

    try {
      window.localStorage.setItem(this.storageKey(), JSON.stringify(selections));
    } catch (error) {
      // Storage can be blocked in private contexts; selections still work for the active page.
    }
  }

  restoreSelections() {
    let selections = {};

    try {
      selections = JSON.parse(window.localStorage.getItem(this.storageKey()) || '{}');
    } catch (error) {
      selections = {};
    }

    Object.entries(selections).forEach(([groupKey, variantId]) => {
      const option = Array.from(this.querySelectorAll('[data-bundle-option]:not(:disabled)')).find((input) => {
        return input.dataset.groupKey === groupKey && input.dataset.variantId === variantId;
      });
      if (option) option.checked = true;
    });
  }

  validate() {
    const missingGroup = this.groups.find((group) => {
      return group.dataset.required === 'true' && !group.querySelector('[data-bundle-option]:checked');
    });

    if (missingGroup) {
      const title = missingGroup.dataset.groupTitle || 'bundle group';
      this.showError(this.labels.selectErrorTemplate.replace('{group}', title));
      missingGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    this.hideError();
    return true;
  }

  async onSubmit(event) {
    event.preventDefault();
    BundleDebug.log('Bundle form submit intercepted');

    if (!this.validate()) return;

    const selectedOptions = this.selectedOptions();
    this.onVariantChange();
    const bundleId = this.createBundleId();
    const parentVariantId = Number(new FormData(this.form).get('id'));

    if (!parentVariantId) {
      this.showError('Please choose an available bundle variant.');
      return;
    }

    const items = [
      {
        id: parentVariantId,
        quantity: this.quantity,
        properties: {
          _bundle_role: 'parent',
          _bundle_id: bundleId,
          _bundle_children: selectedOptions.map((option) => option.variantId).join(',')
        }
      },
      ...selectedOptions.map((option) => ({
        id: option.variantId,
        quantity: this.quantity,
        properties: {
          _bundle_role: 'child',
          _bundle_parent: bundleId,
          _bundle_child: 'true',
          _bundle_group: option.groupTitle,
          _bundle_parent_variant: String(parentVariantId)
        }
      }))
    ];

    BundleDebug.log('Prepared bundle cart items', items);

    this.setLoading(true);

    try {
      const sections = this.sectionsToRender();
      const response = await fetch(BundleRoutes.add(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname
        })
      });

      const payload = await response.json();
      BundleDebug.log('Cart add response', { ok: response.ok, payload });

      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'Unable to add bundle to cart.');
      }

      await this.refreshCart(payload);
      this.dispatchEvent(new CustomEvent('bundle:added', { bubbles: true, detail: { payload, bundleId } }));
    } catch (error) {
      BundleDebug.warn('Bundle add failed', error);
      this.showError(error.message);
    } finally {
      this.setLoading(false);
    }
  }

  createBundleId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `bundle-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;

    this.submitButton.classList.toggle('loading', isLoading);
    this.submitButton.toggleAttribute('aria-disabled', isLoading);
    this.submitButton.disabled = isLoading;

    const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
    spinner?.classList.toggle('hidden', !isLoading);
  }

  showError(message) {
    if (!this.errorWrapper || !this.errorText) return;
    this.errorText.textContent = message;
    this.errorWrapper.hidden = false;
  }

  hideError() {
    if (!this.errorWrapper || !this.errorText) return;
    this.errorText.textContent = '';
    this.errorWrapper.hidden = true;
  }

  async refreshCart(addPayload) {
    const cartNotification = document.querySelector('cart-notification');
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartNotification?.renderContents) {
      cartNotification.renderContents(addPayload);
      return;
    }

    if (cartDrawer?.renderContents) {
      const response = await fetch(`${BundleRoutes.cart()}?sections=cart-drawer,cart-icon-bubble`);
      const sections = await response.json();
      cartDrawer.renderContents({ sections });
      return;
    }

    if (this.cartType === 'drawer') {
      window.location = BundleRoutes.cart();
      return;
    }

    window.location = BundleRoutes.cart();
  }

  sectionsToRender() {
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification?.getSectionsToRender) {
      return cartNotification.getSectionsToRender().map((section) => section.id);
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.getSectionsToRender) {
      return cartDrawer.getSectionsToRender().map((section) => section.id);
    }

    return ['cart-icon-bubble'];
  }
}

if (!customElements.get('bundle-form')) {
  customElements.define('bundle-form', BundleForm);
}

const BundleRoutes = {
  root() {
    return window.Shopify?.routes?.root || window.routes?.root_url || '/';
  },

  path(path) {
    const root = this.root();
    const cleanRoot = root.endsWith('/') ? root.slice(0, -1) : root;
    return `${cleanRoot}${path}`;
  },

  cart() {
    return window.routes?.cart_url || this.path('/cart');
  },

  add() {
    const url = window.routes?.cart_add_url || this.path('/cart/add');
    return url.endsWith('.js') ? url : `${url}.js`;
  },

  cartJson() {
    const url = window.routes?.cart_url || this.path('/cart');
    return url.endsWith('.js') ? url : `${url}.js`;
  },

  update() {
    const url = window.routes?.cart_update_url || this.path('/cart/update');
    return url.endsWith('.js') ? url : `${url}.js`;
  }
};

function formatBundleMoney(cents) {
  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || window.Shopify.money_format);
  }

  const currency = window.Shopify?.currency?.active || window.Shopify?.currency?.shopCurrency || 'DKK';
  const locale = document.documentElement.lang || undefined;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(cents / 100);
  } catch (error) {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

class BundleCart extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    BundleDebug.log('BundleCart connected');
    this.init();
  }

  async init() {
    try {
      this.cart = await fetch(BundleRoutes.cartJson()).then((response) => response.json());
      BundleDebug.log('Fetched cart JSON', this.cart);
      BundleDebug.table(
        (this.cart.items || []).map((item, index) => ({
          index: index + 1,
          title: item.product_title || item.title,
          price: formatBundleMoney(item.final_price),
          line_price: formatBundleMoney(item.final_line_price),
          role: item.properties?._bundle_role || '',
          bundle_id: item.properties?._bundle_id || '',
          bundle_parent: item.properties?._bundle_parent || '',
          child: item.properties?._bundle_child || ''
        }))
      );
    } catch (error) {
      this.cart = null;
      BundleDebug.warn('Unable to fetch cart JSON', error);
    }

    this.prepareLines();
    this.bindRemoveButtons();
    this.bindQuantityInputs();
    this.updateDisplayedSubtotal();
    this.observeCartChanges();
  }

  prepareLines() {
    const markers = Array.from(document.querySelectorAll('[data-bundle-line-key]'));
    BundleDebug.log('Preparing cart lines', { markerCount: markers.length });

    markers.forEach((marker) => {
      const row = marker.closest('.cart-item, tr, li, .cart__row, .ajaxcart__row') || marker.parentElement;
      if (!row) return;

      row.classList.add('bundle-aware-line');

      if (marker.hasAttribute('data-bundle-child-line')) {
        row.classList.add('bundle-child-line');
        this.displayChildLineAsFree(row);
        BundleDebug.log('Marked child row from Liquid marker', row);
      }
    });

    this.prepareRowsFromCartJson();
  }

  prepareRowsFromCartJson() {
    if (!this.cart?.items?.length) return;

    const rows = this.cartRows();
    BundleDebug.log('Candidate cart rows found', rows.length, rows);
    if (!rows.length) return;

    const matchedRows = new Set();

    this.cart.items.forEach((item, index) => {
      const row = this.findRowForItem(item, index, rows, matchedRows);
      if (!item) return;
      if (!row) {
        BundleDebug.warn('No row matched for cart item', {
          index: index + 1,
          title: item.product_title || item.title,
          properties: item.properties
        });
        return;
      }

      matchedRows.add(row);

      const bundleId = item.properties?._bundle_id || item.properties?._bundle_parent;
      const isParent = item.properties?._bundle_role === 'parent' && item.properties?._bundle_id;
      const isChild = item.properties?._bundle_role === 'child' || item.properties?._bundle_child;

      row.dataset.cartLineIndex = String(index + 1);
      row.dataset.cartLineKey = item.key;

      if (bundleId) {
        row.dataset.bundleParentId = bundleId;
        row.classList.add('bundle-aware-line');
        BundleDebug.log('Matched bundle row', {
          index: index + 1,
          title: item.product_title || item.title,
          isParent,
          isChild,
          bundleId,
          row
        });
      }

      if (isParent) {
        row.dataset.bundleParentLine = 'true';
        this.hideBundleProperties(row);
      }

      if (isChild) {
        row.dataset.bundleChildLine = 'true';
        row.classList.add('bundle-child-line');
        this.displayChildLineAsFree(row);
        this.lockChildLine(row);
        this.hideBundleProperties(row);
        BundleDebug.log('Displayed child row as free', {
          index: index + 1,
          title: item.product_title || item.title,
          rowText: row.textContent
        });
      }
    });
  }

  cartRows() {
    const scopedRows = Array.from(this.querySelectorAll('.cart-item, tr.cart-item, tbody tr, .cart__row, .ajaxcart__row, [data-cart-item], [data-line-item]'));
    if (scopedRows.length) return scopedRows.filter((row) => !row.closest('thead, tfoot'));

    const cartRoot = findBundleCartRoot();
    if (!cartRoot) return [];

    const directRows = Array.from(cartRoot.querySelectorAll('.cart-item, tr.cart-item, tbody tr, .cart__row, .ajaxcart__row, [data-cart-item], [data-line-item]')).filter((row) => {
      return !row.closest('thead, tfoot') && !row.closest('header, footer');
    });

    if (directRows.length) return directRows;

    BundleDebug.log('Using text/price fallback row detection');
    return this.cartRowsFromPriceCells();
  }

  cartRowsFromPriceCells() {
    if (!this.cart?.items?.length) return [];

    const rows = [];
    const seen = new Set();
    const cartRoot = findBundleCartRoot();
    if (!cartRoot) return rows;

    this.cart.items.forEach((item) => {
      const title = this.normalizeText(item.product_title || item.title);
      if (!title) return;
      const titleWords = this.titleWords(title);

      const candidates = Array.from(cartRoot.querySelectorAll('div, li, tr, article, section')).filter((element) => {
        const text = this.normalizeText(element.textContent);
        return this.textMatchesItem(text, title, titleWords, item);
      });

      const row = candidates
        .sort((a, b) => this.normalizeText(a.textContent).length - this.normalizeText(b.textContent).length)
        .find((element) => !seen.has(element));

      if (row) {
        seen.add(row);
        rows.push(row);
      }
    });

    return rows;
  }

  findRowForItem(item, index, rows, matchedRows) {
    const keyMatch = rows.find((row) => row.dataset.cartLineKey === item.key && !matchedRows.has(row));
    if (keyMatch) return keyMatch;

    const title = this.normalizeText(item.product_title || item.title);
    const variantTitle = item.variant_title ? this.normalizeText(item.variant_title) : '';
    const titleWords = this.titleWords(title);

    const textMatch = rows.find((row) => {
      if (matchedRows.has(row)) return false;
      const text = this.normalizeText(row.textContent);
      if (!this.textMatchesItem(text, title, titleWords, item)) return false;
      if (variantTitle && !text.includes(variantTitle)) return false;
      return true;
    });
    if (textMatch) {
      BundleDebug.log('Matched row by text', {
        title: item.product_title || item.title,
        rowText: textMatch.textContent
      });
      return textMatch;
    }

    BundleDebug.warn('Falling back to row index match', {
      index: index + 1,
      title: item.product_title || item.title
    });
    return matchedRows.has(rows[index]) ? null : rows[index];
  }

  normalizeText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  titleWords(title) {
    return title
      .split(/[^\p{L}\p{N}]+/u)
      .filter((word) => word.length >= 3);
  }

  textMatchesItem(text, title, titleWords, item) {
    const unitPrice = this.normalizePrice(formatBundleMoney(item.final_price));
    const linePrice = this.normalizePrice(formatBundleMoney(item.final_line_price));
    const textPrice = this.normalizePrice(text);
    const hasPrice = textPrice.includes(unitPrice) || textPrice.includes(linePrice);

    if (!hasPrice) return false;
    if (text.includes(title)) return true;

    const matchedWords = titleWords.filter((word) => text.includes(word));
    return matchedWords.length >= Math.min(2, titleWords.length);
  }

  normalizePrice(value) {
    return String(value || '').toLowerCase().replace(/\s+/g, '').replace(/,/g, '.');
  }

  displayChildLineAsFree(row) {
    row.setAttribute('data-bundle-display-free', 'true');

    const priceSelectors = [
      '[data-bundle-price]',
      '[data-bundle-line-total]',
      '[data-cart-item-regular-price]',
      '[data-cart-item-final-price]',
      '.cart-item__price-wrapper .price',
      '.cart-item__totals .price',
      '.cart__price',
      '.cart__final-price',
      '.ajaxcart__price',
      'td:nth-last-child(3)',
      'td:last-child',
      '.price'
    ];
    const zeroPrice = formatBundleMoney(0);

    row.querySelectorAll(priceSelectors.join(',')).forEach((element) => {
      element.textContent = zeroPrice;
      element.setAttribute('data-bundle-debug-price-replaced', 'true');
    });

    this.replaceTextPriceNodes(row);
  }

  hideBundleProperties(row) {
    const privatePropertyPattern = /_bundle_(role|id|children|parent|child|group|parent_variant)/i;

    Array.from(row.querySelectorAll('li, p, dd, dt')).forEach((element) => {
      const text = this.normalizeText(element.textContent);
      if (!privatePropertyPattern.test(text)) return;

      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
      element.style.display = 'none';
    });

    Array.from(row.querySelectorAll('span')).forEach((element) => {
      const text = this.normalizeText(element.textContent);
      if (!privatePropertyPattern.test(text)) return;
      if (text.length > 120) return;

      const parentText = this.normalizeText(element.parentElement?.textContent || '');
      const target = parentText.length <= 180 ? element.parentElement : element;

      target.hidden = true;
      target.setAttribute('aria-hidden', 'true');
      target.style.display = 'none';
    });
  }

  replaceTextPriceNodes(row) {
    const walker = document.createTreeWalker(row, NodeFilter.SHOW_TEXT);
    const pricePattern = /(?:rs\.?|pkr|kr\.?|dkk|€|eur|\$|usd|£|gbp)?\s*\d+(?:[.,]\d{1,2})?\s*(?:rs\.?|pkr|kr\.?|dkk|€|eur|\$|usd|£|gbp)?/gi;
    const nodes = [];
    const zeroPrice = formatBundleMoney(0);

    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (pricePattern.test(node.nodeValue)) {
        nodes.push(node);
      }
      pricePattern.lastIndex = 0;
    }

    nodes.forEach((node) => {
      node.nodeValue = node.nodeValue.replace(pricePattern, zeroPrice);
    });

    BundleDebug.log('Replaced text price nodes', { count: nodes.length, row });
  }

  lockChildLine(row) {
    row.querySelectorAll('a[href*="/cart/change"], a[href*="quantity=0"], cart-remove-button, [data-cart-remove], .cart-remove-button, .remove').forEach((element) => {
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
      element.style.display = 'none';
    });

    row.querySelectorAll('a, button').forEach((element) => {
      if (this.normalizeText(element.textContent) === 'remove') {
        element.hidden = true;
        element.setAttribute('aria-hidden', 'true');
        element.style.display = 'none';
      }
    });

    row.querySelectorAll('input[name="updates[]"], input[type="number"], quantity-input button, .quantity button').forEach((element) => {
      element.disabled = true;
      element.setAttribute('aria-disabled', 'true');
    });

    BundleDebug.log('Locked child row controls', row);
  }

  bindRemoveButtons() {
    if (this.removeButtonsBound) return;
    this.removeButtonsBound = true;

    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('cart-remove-button, [data-cart-remove], a[href*="/cart/change"], a[href*="quantity=0"], .cart-remove-button, .remove');
      if (!removeButton) return;

      const row = removeButton.closest('.cart-item, tr, li, .cart__row, .ajaxcart__row, [data-cart-item], [data-line-item]');
      const bundleId = this.bundleIdFromRow(row) || this.bundleIdFromRemoveButton(removeButton, row);
      if (!bundleId) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (row?.dataset.bundleChildLine === 'true') {
        BundleDebug.log('Blocked child line remove', row);
        return;
      }

      BundleDebug.log('Removing complete bundle', { bundleId });
      this.removeBundle(bundleId);
    }, true);
  }

  bindQuantityInputs() {
    document.addEventListener('change', (event) => {
      const input = event.target.closest('input[name="updates[]"]');
      if (!input) return;

      const row = input.closest('.cart-item, tr, li, .cart__row, .ajaxcart__row');
      const bundleId = this.bundleIdFromRow(row);
      if (!bundleId) return;

      if (row?.dataset.bundleChildLine === 'true') {
        input.value = input.defaultValue || input.value;
        event.preventDefault();
        BundleDebug.log('Blocked child line quantity change', row);
        return;
      }

      BundleDebug.log('Syncing bundle quantity', { bundleId, quantity: Number(input.value || 1) });
      this.syncBundleQuantity(bundleId, Number(input.value || 1));
    });
  }

  bundleIdFromRow(row) {
    if (!row) return null;
    if (row.dataset.bundleParentId) return row.dataset.bundleParentId;
    const property = row.querySelector('[data-bundle-parent-id]');
    return property?.dataset.bundleParentId || null;
  }

  bundleIdFromRemoveButton(removeButton, row) {
    if (!this.cart?.items?.length) return null;

    const lineIndex = this.lineIndexFromRemoveButton(removeButton, row);
    if (!lineIndex) return null;

    const item = this.cart.items[lineIndex - 1];
    return item?.properties?._bundle_id || item?.properties?._bundle_parent || null;
  }

  lineIndexFromRemoveButton(removeButton, row) {
    const rowIndex = Number(row?.dataset.cartLineIndex || row?.dataset.cartItemIndex || row?.dataset.lineItemIndex);
    if (rowIndex > 0) return rowIndex;

    const href = removeButton.getAttribute('href') || '';
    try {
      const url = new URL(href, window.location.origin);
      const line = Number(url.searchParams.get('line'));
      if (line > 0) return line;
    } catch (error) {
      // Ignore malformed hrefs and fall back to cart row order.
    }

    const rows = this.cartRows();
    const rowPosition = rows.indexOf(row);
    return rowPosition >= 0 ? rowPosition + 1 : null;
  }

  updateDisplayedSubtotal() {
    if (!this.cart?.items?.length) return;

    const childTotal = this.cart.items.reduce((total, item) => {
      const isChild = item.properties?._bundle_role === 'child' || item.properties?._bundle_child;
      return isChild ? total + item.final_line_price : total;
    }, 0);

    if (!childTotal) return;

    const adjustedTotal = Math.max(this.cart.total_price - childTotal, 0);
    const subtotalText = formatBundleMoney(adjustedTotal);
    const selectors = [
      '[data-cart-subtotal]',
      '.totals__subtotal-value',
      '.cart__subtotal',
      '.cart-subtotal__price',
      '.cart__footer .price',
      '.cart__footer [class*="subtotal"]',
      '#CartSubtotal'
    ];

    document.querySelectorAll(selectors.join(',')).forEach((element) => {
      element.textContent = subtotalText;
      element.setAttribute('data-bundle-debug-subtotal-replaced', 'true');
    });

    BundleDebug.log('Updated displayed subtotal', {
      original: formatBundleMoney(this.cart.total_price),
      childTotal: formatBundleMoney(childTotal),
      adjusted: subtotalText
    });
  }

  observeCartChanges() {
    if (this.observer) return;

    let timeout;
    this.observer = new MutationObserver(() => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        BundleDebug.log('Cart DOM changed, reapplying bundle display');
        this.prepareLines();
        this.updateDisplayedSubtotal();
      }, 100);
    });

    const cartRoot = findBundleCartRoot();
    if (cartRoot) {
      this.observer.observe(cartRoot, { childList: true, subtree: true, characterData: true });
    }
  }

  async removeBundle(bundleId) {
    const cart = await fetch(BundleRoutes.cartJson()).then((response) => response.json());
    const updates = {};

    cart.items.forEach((item) => {
      if (item.properties?._bundle_id === bundleId || item.properties?._bundle_parent === bundleId) {
        updates[item.key] = 0;
      }
    });

    BundleDebug.log('Bundle remove updates', updates);

    if (!Object.keys(updates).length) {
      BundleDebug.warn('No bundle lines found to remove', { bundleId, cart });
      return;
    }

    await fetch(BundleRoutes.update(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });

    window.location.reload();
  }

  async syncBundleQuantity(bundleId, quantity) {
    const cart = await fetch(BundleRoutes.cartJson()).then((response) => response.json());
    const updates = {};

    cart.items.forEach((item) => {
      if (item.properties?._bundle_id === bundleId || item.properties?._bundle_parent === bundleId) {
        updates[item.key] = quantity;
      }
    });

    BundleDebug.log('Bundle quantity updates', updates);

    await fetch(BundleRoutes.update(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });

    window.location.reload();
  }
}

if (!customElements.get('bundle-cart')) {
  customElements.define('bundle-cart', BundleCart);
}

function initBundleCartFallback() {
  if (document.querySelector('bundle-cart')) return;
  if (!findBundleCartRoot()) {
    BundleDebug.log('Bundle cart fallback skipped: no cart-like markup found');
    return;
  }

  const bundleCart = document.createElement('bundle-cart');
  bundleCart.hidden = true;
  document.body.appendChild(bundleCart);
  BundleDebug.log('Bundle cart fallback appended');
}

function findBundleCartRoot() {
  const cartForm = document.querySelector(
    'form[action="/cart"], form[action$="/cart"], form[action*="/cart?"], form[action^="/cart?"]'
  );

  if (cartForm) return cartForm;

  const isCartPath = window.location.pathname.replace(/\/$/, '') === '/cart';
  if (!isCartPath) return null;

  return (
    document.querySelector('[data-cart-page], .cart-page, .cart-template, .cart__items, table.cart, .cart') ||
    document.querySelector('main')
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundleCartFallback);
} else {
  initBundleCartFallback();
}
})();
